import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teamAPI } from '../services/api';
import './ProfileDropdown.css';
import { usePWAInstall } from '../App';

function ProfileDropdown() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState('free');
  const [planLoading, setPlanLoading] = useState(true);
  const dropdownRef = useRef(null);
  // eslint-disable-next-line
  const [teamSport, setTeamSport] = useState('');

  const { installPrompt, handleInstallClick: handlePWAInstall } = usePWAInstall();

  const handleInstallClick = async () => {
    await handlePWAInstall();
    setIsOpen(false);
  };

  // Fetch subscription plan
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await teamAPI.getLimitInfo();
        setSubscriptionPlan(response.data.subscriptionPlan || 'free');

        // Fetch first team's sport for icon
        const teamsResponse = await teamAPI.getAll();
        const teams = teamsResponse.data || [];
        if (teams.length > 0) {
          const sortedTeams = [...teams].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          setTeamSport(sortedTeams[0].sport || 'Baseball');
        }
      } catch (err) {
        console.error('Error fetching subscription plan:', err);
        setSubscriptionPlan('free');
      } finally {
        setPlanLoading(false);
      }
    };

    fetchPlan();

    // Only poll if on membership page (where changes happen)
    const isOnMembershipPage = window.location.pathname === '/membership';
    let interval;

    if (isOnMembershipPage) {
      interval = setInterval(fetchPlan, 3000); // Poll every 3 seconds on membership page
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // Also re-fetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      const fetchPlan = async () => {
        try {
          const response = await teamAPI.getLimitInfo();
          setSubscriptionPlan(response.data.subscriptionPlan || 'free');
        } catch (err) {
          console.error('Error fetching subscription plan:', err);
        }
      };
      fetchPlan();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleTeamPortalClick = async () => {
    try {
      setIsOpen(false);
      const response = await teamAPI.getAll();
      const teams = response.data;

      if (teams && teams.length > 0) {
        // Sort by creation date to get first created team
        const sortedTeams = [...teams].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        navigate(`/team/${sortedTeams[0].id}`);
      } else {
        // No teams yet, show empty state on home and scroll to it
        navigate('/');
        setTimeout(() => {
          const teamsSection = document.getElementById('teams-section');
          if (teamsSection) {
            teamsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      navigate('/'); // Fallback to home
    }
  };

  return (
    <div className="profile-dropdown" ref={dropdownRef}>
      <button className="profile-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className="hamburger-icon">☰</span>
        {!planLoading && (
          <span className={`plan-badge plan-badge-${subscriptionPlan}`}>
            {subscriptionPlan.toUpperCase()}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-header">
            <div className="user-info">
              <div className="user-name-large">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="user-email">{user?.email}</div>
              <div className={`plan-indicator plan-${subscriptionPlan}`}>
                {subscriptionPlan.toUpperCase()} PLAN
              </div>
            </div>
          </div>

          <div className="dropdown-divider" />

          {(user?.role === 'coach' || user?.role === 'both' || user?.role === 'unset') && (
            <button className="dropdown-item" onClick={handleTeamPortalClick}>
              <span>Team Portal</span>
            </button>
          )}

          {(user?.role === 'parent' || user?.role === 'both' || user?.role === 'unset') && (
            <button
              className="dropdown-item"
              onClick={() => handleMenuClick('/analytics/players')}
            >
              <span>PlayMaker Analytics</span>
            </button>
          )}

          <button
            className="dropdown-item"
            onClick={() => handleMenuClick('/account')}
          >
            <span>Account Settings</span>
          </button>

          <button
            className="dropdown-item"
            onClick={() => handleMenuClick('/membership')}
          >
            <span>Membership</span>
          </button>

          {installPrompt && (
            <button
              className="dropdown-item"
              onClick={handleInstallClick}
            >
              <span>📲 Add to Home Screen</span>
            </button>
          )}
          <button
            className="dropdown-item"
            onClick={() => {
              window.location.href = '/contact';
              setIsOpen(false);
            }}
          >
            <span>Contact Us</span>
          </button>

          {subscriptionPlan === 'organization' && (
            <>
              <div className="dropdown-divider" />
              <button
                className="dropdown-item"
                onClick={() => handleMenuClick('/organization/settings')}
              >
                <span>Organization Settings</span>
              </button>
              <button
                className="dropdown-item"
                onClick={() => handleMenuClick('/organization/bulk-import')}
              >
                <span>Bulk Import Teams</span>
              </button>
              <button
                className="dropdown-item"
                onClick={() => handleMenuClick('/organization/manage-teams')}
              >
                <span>Manage Teams</span>
              </button>
            </>
          )}

          {user?.isAdmin && (
            <>
              <div className="dropdown-divider"></div>
              <button
                className="dropdown-item"
                onClick={() => {
                  navigate('/admin');
                  setIsOpen(false);
                }}
              >
                Admin Portal
              </button>
            </>
          )}

          <div className="dropdown-divider" />

          <button className="dropdown-item logout-item" onClick={handleLogout}>
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileDropdown;
